/**
 * Stable composite keys for the multi-tax selection tree.
 *
 * `SALES_TAX_KEY` and the two helpers are shared across MultiTaxTable,
 * MultiTaxJurisdictionPicker, and useMultiTaxBlock. They live here rather
 * than inside any one component so none of those files pulls in React just to
 * import a string constant and two pure functions.
 */

/**
 * Synthetic TaxType id representing Sales-and-Use Tax inside the tree.
 * TPS never returns SUT in its multi-tax response — it is the implicit
 * default the rest of ECM was built around — so we inject this row
 * client-side and route its juris picker to JurisdictionSelect.
 */
export const SALES_TAX_KEY = "Sales Tax";

/**
 * Display label for the synthetic Sales-and-Use Tax entry. The key ({@link SALES_TAX_KEY}) stays
 * "Sales Tax" (the value the search API matches on), but the UI shows the full name.
 */
export const SALES_TAX_LABEL = "Sales and use tax";

/**
 * Reserved sentinel `source_mapping_id` for Sales & Use Tax in the certificate-search
 * `taxSubTaxMappingIds` filter. Negative so it can't collide with a real (non-negative) mapping id.
 * MUST stay in sync with the backend `Constants.SalesTaxSourceMappingId`.
 */
export const SALES_TAX_SOURCE_MAPPING_ID = -1;

/**
 * Stable composite key for a selection at the (TaxType, SubType) level.
 * SubType may be null for TaxTypes that have no sub-types — encoded as the
 * empty string so the key is always a plain string.
 */
export const taxTypeNodeKey = (taxType: string, subType: string | null): string =>
    `${taxType}|${subType ?? ""}`;

/** Sales Tax has no sub-types — its single key is "Sales Tax|". */
export const salesTaxNodeKey = (): string => taxTypeNodeKey(SALES_TAX_KEY, null);
